import { useEffect, useRef, useState } from 'react'
import { v3 } from '../../copyV3'
import type { PlanItemData } from '../../lib/plan'
import { Symbol } from '../../ui/Symbol'
import { hasSmaller, sized } from '../../lib/imageSize'

/**
 * Photos step 1 (Alvin 10/02: "tastefully and polished"; AGENTS.md: the venue's own link-preview image or a credited
 * Wikimedia Commons photo, always with the source, never generated). One fixed 16:9 slot (was 4:3; journey J1 10/08: a 4:3 photo pushed the first title below the fold at 375x812), so a card never shifts as a
 * photo arrives and rows stay even: the stored image (lazy, async decode, explicit size), or the category symbol tile in
 * the same slot when there is none or it fails to load. No resizing proxy yet; the URL is the one we stored.
 */
export function Photo({ item, size = 'card', className = '', onFail }: { item: PlanItemData; size?: 'card' | 'detail'; className?: string; onFail?: () => void }) {
  const [failed, setFailed] = useState(false)
  // lead's call 10/09: a photo far from the slot's shape (portrait, square, a wide poster or flyer) is shown whole on a
  // blurred copy of itself, never cropped; a photo close to the slot's shape fills it
  const [whole, setWhole] = useState(false)
  const stored = item.image_url && /^https:\/\//.test(item.image_url) ? item.image_url : null
  // the host's own smaller rendition on cards (lib/imageSize); if that ever fails, the stored photo once, then the tile
  const [useStored, setUseStored] = useState(false)
  const src = stored ? (useStored ? stored : sized(stored, size)) : null
  const box = `gh-photo relative block aspect-[16/9] w-full overflow-hidden bg-sunk ${className}`
  // photo weight (lead 10/08): a card asks for its photo only when it is about to be seen (300 px below the screen, or the
  // next card in a rail), not at the browser's own lazy distance (1,250 px and more, which fetched 2.9 MB on one home page)
  const holder = useRef<HTMLSpanElement>(null)
  const [near, setNear] = useState(size === 'detail')
  useEffect(() => {
    if (near || !holder.current) return
    if (typeof IntersectionObserver === 'undefined') { setNear(true); return }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setNear(true); io.disconnect() } }, { rootMargin: '300px 120px' })
    io.observe(holder.current)
    return () => io.disconnect()
  }, [near])
  if (!src || failed) {
    return (
      <span data-review-id="photo-tile" aria-hidden="true" className={`${box} flex items-center justify-center text-ink-3`}>
        <Symbol cat={item.category} size={size === 'detail' ? 56 : 40} />
      </span>
    )
  }
  if (!near) return <span ref={holder} data-review-id="photo-wait" aria-hidden="true" className={box} />
  return (
    <span className={box}>
      {whole && <img src={src} alt="" aria-hidden="true" referrerPolicy="no-referrer" className="absolute inset-0 size-full scale-110 object-cover opacity-60 blur-xl" />}
      <img data-review-id="photo" data-fit={whole ? 'contain' : 'cover'} src={src} alt="" width={size === 'detail' ? 800 : 400} height={size === 'detail' ? 450 : 225}
        loading={size === 'detail' ? 'eager' : 'lazy'} decoding="async" referrerPolicy="no-referrer"
        fetchPriority={size === 'card' && stored && !hasSmaller(stored) ? 'low' : 'auto'}
        onLoad={(e) => { const i = e.currentTarget; const r = i.naturalWidth / Math.max(1, i.naturalHeight); const slot = 16 / 9; setWhole(r < slot * 0.88 || r > slot * 1.12) }}
        onError={() => { if (stored && src !== stored) setUseStored(true); else { setFailed(true); onFail?.() } }} className={`absolute inset-0 size-full ${whole ? 'object-contain' : 'object-cover'}`} />
    </span>
  )
}

/**
 * Where a photo came from (B-226: CC BY / BY-SA ask for author, licence and a link). A Commons photo reads
 * "<author>, <licence>" (a long name is shortened, never swapped for the host) and links to the file's own page, which
 * carries the full credit; any other photo reads the site's name and links to the page we took it from.
 */
export function photoCredit(item: PlanItemData): { text: string; href: string | null } | null {
  if (!item.image_url) return null
  const credit = (item.image_credit ?? '').trim()
  const file = /^https:\/\/commons\.wikimedia\.org\/wiki\/Special:FilePath\/([^?#]+)/.exec(item.image_url)?.[1]
  const m = /^Wikimedia Commons(?: \/ (.*))?, ([^,]+)$/.exec(credit)
  if (file || m) {
    const href = file ? `https://commons.wikimedia.org/wiki/File:${file}` : null
    if (!m) return { text: 'Wikimedia Commons', href }
    const author = /^[a-z]{2,3}$/.test(m[1] ?? '') ? '' : (m[1] ?? '').trim() // a lone language code ("en") names nobody
    const short = author.length > 32 ? `${author.slice(0, 31).trimEnd()}…` : author
    return { text: short ? `${short}, ${m[2]}` : `Wikimedia Commons, ${m[2]}`, href }
  }
  let host = ''
  try { host = new URL(item.image_url).hostname.replace(/^www\./, '') } catch { /* stored links are https */ }
  const text = credit && credit.length <= 40 ? credit : host || item.source_name || ''
  return text ? { text, href: item.url || null } : null
}

/** The larger photo on an activity's page, with "Photo: <credit>" under it. */
export function DetailPhoto({ item }: { item: PlanItemData }) {
  const credit = photoCredit(item)
  const [failed, setFailed] = useState(false)
  // no photo (or a broken one): the page starts with its words; the big symbol tile is for even card rows only
  if (!item.image_url || failed) return null
  return (
    <figure data-review-id="detail-photo" className="mb-3">
      {/* B-111: capped on short screens so the calendar buttons show without scrolling (a photo far from 16:9 shows whole) */}
      <Photo item={item} size="detail" className="max-h-[min(240px,22svh)]" onFail={() => setFailed(true)} />
      {credit && (
        <figcaption data-review-id="photo-credit" className="t-caption mt-1.5 text-ink-2">
          {credit.href
            ? <a href={credit.href} target="_blank" rel="noopener noreferrer" className="underline decoration-ink-3 underline-offset-2">{v3('v3.photo_credit', { credit: credit.text })}</a>
            : v3('v3.photo_credit', { credit: credit.text })}
        </figcaption>
      )}
    </figure>
  )
}

/** Lead's call 10/09: a row (or list) shows photo slots only when at least half its cards have a photo; otherwise text cards. */
export function photoRow(cards: { item: PlanItemData }[]): boolean {
  return cards.length > 0 && cards.filter((c) => c.item.image_url).length * 2 >= cards.length
}
